import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useHistory } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaGlobe,
  FaPhoneAlt,
} from 'react-icons/fa';

const WHATSAPP_MESSAGE = encodeURIComponent('Olá Ecosistemas, gostaria de saber mais dos seus produtos.');
const WHATSAPP_URL = `https://wa.me/5567984485377?text=${WHATSAPP_MESSAGE}`;

function hexToRgba(hex, alpha) {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const bigint = parseInt(full, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const reveal = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const PageContainer = styled.div`
  min-height: 100vh;
  padding-top: 94px;
  color: #173042;

  @media (max-width: 768px) {
    padding-top: 78px;
  }
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
`;

const HeroSection = styled(Section)`
  padding: 54px 0 44px;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  gap: 48px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const BackButton = styled(motion.button)`
  border: none;
  background: none;
  padding: 0;
  margin-bottom: 28px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${(props) => props.$accent};
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
`;

const EyebrowGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 9px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.84);
  border: 1px solid ${(props) => props.$borderColor};
  color: #45667f;
  font-size: 0.85rem;
  font-weight: 700;
  box-shadow: 0 12px 24px rgba(59, 158, 212, 0.08);
`;

const Title = styled.h1`
  margin: 0 0 18px;
  font-size: clamp(3rem, 5.8vw, 5rem);
  line-height: 0.98;
  font-weight: 900;
  letter-spacing: -0.04em;
  color: #173042;
  max-width: 10ch;
`;

const TitleAccent = styled.span`
  color: ${(props) => props.$accent};
`;

const Lead = styled.p`
  margin: 0 0 18px;
  max-width: 640px;
  color: #4b6882;
  font-size: 1.12rem;
  line-height: 1.8;
`;

const SupportText = styled.p`
  margin: 0 0 28px;
  max-width: 660px;
  color: #30506a;
  font-size: 1rem;
  line-height: 1.75;
  font-weight: 600;
`;

const MetricRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 30px;
`;

const MetricPill = styled.div`
  padding: 12px 15px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid ${(props) => props.$borderColor};
  color: #30506a;
  font-size: 0.93rem;
  font-weight: 700;
  box-shadow: 0 16px 30px rgba(59, 158, 212, 0.08);
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  background: linear-gradient(135deg, ${(props) => props.$accent} 0%, ${(props) => props.$accentSoft} 100%);
  color: #ffffff;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 18px 34px ${(props) => props.$shadowColor};
  transition: transform 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 40px ${(props) => props.$shadowStrong};
    color: #ffffff;
    text-decoration: none;
  }
`;

const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid ${(props) => props.$borderColor};
  color: ${(props) => props.$accent};
  font-weight: 800;
  text-decoration: none;
  transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${(props) => props.$borderStrong};
    box-shadow: 0 16px 30px ${(props) => props.$shadowSoft};
    color: ${(props) => props.$accent};
    text-decoration: none;
  }
`;

const HeroCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid ${(props) => props.$borderColor};
  border-radius: 34px;
  padding: 26px;
  box-shadow: 0 28px 60px ${(props) => props.$shadowSoft};
  backdrop-filter: blur(18px);
`;

const HeroTopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const MiniCard = styled.div`
  padding: 16px;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f4fbff 100%);
  border: 1px solid ${(props) => props.$borderColor};
`;

const MiniLabel = styled.p`
  margin: 0 0 8px;
  color: #6a8899;
  font-size: 0.82rem;
  font-weight: 700;
`;

const MiniValue = styled.p`
  margin: 0;
  color: #173042;
  font-size: 1.3rem;
  font-weight: 900;
  line-height: 1.2;
`;

const DashboardCard = styled.div`
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(180deg, #eaf7ff 0%, #dff2fd 100%);
  border: 1px solid ${(props) => props.$borderStrong};
`;

const DashboardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const DashboardTitle = styled.h3`
  margin: 0;
  color: #173042;
  font-size: 1rem;
  font-weight: 800;
`;

const StatusDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${(props) => props.$accent};
  box-shadow: 0 0 0 8px ${(props) => props.$shadowSoft};
`;

const ScheduleList = styled.div`
  display: grid;
  gap: 12px;
`;

const ScheduleItem = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const ScheduleTime = styled.div`
  color: ${(props) => props.$accent};
  font-weight: 800;
  font-size: 0.95rem;
`;

const ScheduleText = styled.div`
  display: grid;
  gap: 4px;
`;

const ScheduleTitle = styled.div`
  color: #173042;
  font-size: 0.95rem;
  font-weight: 800;
`;

const ScheduleMeta = styled.div`
  color: #567087;
  font-size: 0.84rem;
`;

const ContentSection = styled(Section)`
  padding: 44px 0 28px;
`;

const SectionHeader = styled.div`
  max-width: 760px;
  margin: 0 auto 34px;
  text-align: center;
`;

const EyebrowTag = styled.span`
  display: inline-flex;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${(props) => props.$background};
  color: ${(props) => props.$accent};
  font-size: 0.84rem;
  font-weight: 800;
  margin-bottom: 14px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  color: #173042;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.03em;
`;

const SectionLead = styled.p`
  margin: 0;
  color: #5a758d;
  font-size: 1.02rem;
  line-height: 1.8;
`;

const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 24px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const IntroCard = styled.div`
  padding: 30px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f4fbff 100%);
  border: 1px solid ${(props) => props.$borderColor};
  box-shadow: 0 16px 34px ${(props) => props.$shadowSoft};
`;

const IntroTitle = styled.h3`
  margin: 0 0 12px;
  color: #173042;
  font-size: 1.5rem;
  font-weight: 800;
`;

const IntroText = styled.p`
  margin: 0;
  color: #567087;
  line-height: 1.85;
  font-size: 1rem;
`;

const QuickList = styled.div`
  display: grid;
  gap: 14px;
`;

const QuickItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid ${(props) => props.$borderColor};
  color: #30506a;
  font-weight: 700;
  line-height: 1.6;
`;

const ModuleGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ModuleCard = styled(motion.article)`
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f7fcff 100%);
  border: 1px solid ${(props) => props.$borderColor};
  box-shadow: 0 16px 34px ${(props) => props.$shadowSoft};
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: ${(props) => props.$borderStrong};
    box-shadow: 0 24px 44px ${(props) => props.$shadowStrong};
  }
`;

const ModuleIcon = styled.div`
  width: 58px;
  height: 58px;
  margin-bottom: 18px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$iconBackground};
  color: ${(props) => props.$accent};
  box-shadow: 0 14px 26px ${(props) => props.$shadowSoft};
`;

const ModuleTitle = styled.h3`
  margin: 0 0 12px;
  color: #173042;
  font-size: 1.24rem;
  font-weight: 800;
`;

const ModuleText = styled.p`
  margin: 0 0 16px;
  color: #567087;
  font-size: 0.97rem;
  line-height: 1.8;
`;

const BulletList = styled.div`
  display: grid;
  gap: 10px;
`;

const Bullet = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #30506a;
  font-size: 0.92rem;
  line-height: 1.6;
  font-weight: 700;
`;

const OperationsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 0.95fr) minmax(0, 1.05fr);
  gap: 22px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const AudienceCard = styled.div`
  padding: 30px;
  border-radius: 30px;
  background: linear-gradient(180deg, ${(props) => props.$darkBg} 0%, ${(props) => props.$darkBgSoft} 100%);
  border: 1px solid ${(props) => props.$borderStrong};
  box-shadow: 0 24px 48px ${(props) => props.$shadowStrong};
  color: #ffffff;
`;

const AudienceTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.5rem;
  font-weight: 800;
`;

const AudienceText = styled.p`
  margin: 0 0 20px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.75;
`;

const SectorGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 22px;
`;

const SectorChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  font-size: 0.92rem;
  font-weight: 700;
`;

const AudienceStat = styled.div`
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
`;

const AudienceStatValue = styled.div`
  font-size: 2rem;
  font-weight: 900;
  margin-bottom: 8px;
`;

const AudienceStatText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  line-height: 1.65;
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const SupportCard = styled.div`
  padding: 24px;
  border-radius: 24px;
  background: linear-gradient(180deg, #ffffff 0%, #f7fcff 100%);
  border: 1px solid ${(props) => props.$borderColor};
  box-shadow: 0 16px 34px ${(props) => props.$shadowSoft};
`;

const SupportCardTitle = styled.h3`
  margin: 0 0 10px;
  color: #173042;
  font-size: 1.12rem;
  font-weight: 800;
`;

const SupportCardText = styled.p`
  margin: 0 0 14px;
  color: #567087;
  font-size: 0.95rem;
  line-height: 1.75;
`;

const SupportCardLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  color: ${(props) => props.$accent};
  font-size: 0.92rem;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${(props) => props.$accent};
  }
`;

const NoticeCard = styled.div`
  padding: 32px;
  border-radius: 28px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid ${(props) => props.$borderColor};
  box-shadow: 0 16px 34px ${(props) => props.$shadowSoft};
`;

const NoticeTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1.42rem;
  font-weight: 800;
  color: #173042;
`;

const NoticeText = styled.p`
  margin: 0 0 18px;
  color: #567087;
  line-height: 1.8;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: ${(props) => props.$background};
  border: 1px solid ${(props) => props.$borderColor};
  color: ${(props) => props.$accent};
  font-size: 0.88rem;
  font-weight: 800;
`;

const ContactCard = styled.div`
  padding: 34px;
  border-radius: 30px;
  background: linear-gradient(135deg, #f1f9ff 0%, #ffffff 100%);
  border: 1px solid ${(props) => props.$borderColor};
  box-shadow: 0 18px 36px ${(props) => props.$shadowSoft};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-top: 26px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${(props) => props.$borderColor};
  color: #173042;
  text-decoration: none;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: ${(props) => props.$borderStrong};
    box-shadow: 0 20px 34px ${(props) => props.$shadowSoft};
    color: #173042;
    text-decoration: none;
  }
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$iconBackground};
  color: ${(props) => props.$accent};
  flex-shrink: 0;
`;

const ContactLabel = styled.div`
  color: #6a8899;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 3px;
`;

const ContactValue = styled.div`
  color: #173042;
  font-size: 1rem;
  font-weight: 800;
  word-break: break-word;
`;

function ProductPageTemplate({ data }) {
  const history = useHistory();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const accent = data.theme.accent;
  const accentSoft = data.theme.accentSoft || data.theme.accent;
  const darkBg = data.theme.darkBg || '#173042';
  const darkBgSoft = data.theme.darkBgSoft || '#234b64';
  const pageBackground = data.theme.pageBackground
    || `
      radial-gradient(circle at left 30%, ${hexToRgba(accentSoft, 0.16)} 0%, transparent 26%),
      radial-gradient(circle at right top, ${hexToRgba(accent, 0.18)} 0%, transparent 28%),
      linear-gradient(180deg, #f6fbff 0%, #ffffff 58%, #f9fcff 100%)
    `;
  const borderColor = hexToRgba(accent, 0.14);
  const borderStrong = hexToRgba(accent, 0.24);
  const shadowSoft = hexToRgba(accent, 0.12);
  const shadowStrong = hexToRgba(accent, 0.18);
  const iconBackground = `linear-gradient(135deg, ${hexToRgba(accent, 0.14)} 0%, ${hexToRgba(accentSoft, 0.2)} 100%)`;
  const sectionTagBackground = hexToRgba(accent, 0.08);

  return (
    <PageContainer style={{ background: pageBackground }}>
      <HeroSection>
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <BackButton $accent={accent} variants={reveal} whileHover={{ x: -6 }} onClick={() => history.push('/')}>
            <FaArrowLeft /> Voltar
          </BackButton>

          <HeroGrid>
            <motion.div variants={reveal}>
              <EyebrowGroup>
                {data.hero.eyebrows.map((eyebrow) => (
                  <Eyebrow key={eyebrow} $borderColor={borderColor}>
                    {eyebrow}
                  </Eyebrow>
                ))}
              </EyebrowGroup>

              <Title>
                {data.hero.accent ? <TitleAccent $accent={accent}>{data.hero.accent} </TitleAccent> : null}
                {data.hero.title}
              </Title>

              <Lead>{data.hero.lead}</Lead>
              <SupportText>{data.hero.support}</SupportText>

              <MetricRow>
                {data.hero.metrics.map((metric) => (
                  <MetricPill key={metric} $borderColor={borderColor}>
                    {metric}
                  </MetricPill>
                ))}
              </MetricRow>

              <CTAGroup>
                <PrimaryButton
                  href={data.hero.primaryCta.href}
                  $accent={accent}
                  $accentSoft={accentSoft}
                  $shadowColor={shadowSoft}
                  $shadowStrong={shadowStrong}
                >
                  {data.hero.primaryCta.label}
                </PrimaryButton>
                <SecondaryButton
                  href={data.hero.secondaryCta.href}
                  $accent={accent}
                  $borderColor={borderColor}
                  $borderStrong={borderStrong}
                  $shadowSoft={shadowSoft}
                >
                  {data.hero.secondaryCta.label}
                </SecondaryButton>
              </CTAGroup>
            </motion.div>

            <HeroCard variants={reveal} $borderColor={borderColor} $shadowSoft={shadowSoft}>
              <HeroTopRow>
                {data.hero.visualCards.map((card) => (
                  <MiniCard key={card.label} $borderColor={borderColor}>
                    <MiniLabel>{card.label}</MiniLabel>
                    <MiniValue>{card.value}</MiniValue>
                  </MiniCard>
                ))}
              </HeroTopRow>

              <DashboardCard $borderStrong={borderStrong}>
                <DashboardHeader>
                  <DashboardTitle>{data.hero.panelTitle}</DashboardTitle>
                  <StatusDot $accent={accent} $shadowSoft={shadowSoft} />
                </DashboardHeader>

                <ScheduleList>
                  {data.hero.panelItems.map((item) => (
                    <ScheduleItem key={`${item.label}-${item.title}`}>
                      <ScheduleTime $accent={accent}>{item.label}</ScheduleTime>
                      <ScheduleText>
                        <ScheduleTitle>{item.title}</ScheduleTitle>
                        <ScheduleMeta>{item.meta}</ScheduleMeta>
                      </ScheduleText>
                    </ScheduleItem>
                  ))}
                </ScheduleList>
              </DashboardCard>
            </HeroCard>
          </HeroGrid>
        </motion.div>
      </HeroSection>

      <ContentSection>
        <SectionHeader>
          <EyebrowTag $background={sectionTagBackground} $accent={accent}>
            {data.intro.tag}
          </EyebrowTag>
          <SectionTitle>{data.intro.title}</SectionTitle>
          <SectionLead>{data.intro.lead}</SectionLead>
        </SectionHeader>

        <IntroGrid>
          <IntroCard $borderColor={borderColor} $shadowSoft={shadowSoft}>
            <IntroTitle>{data.intro.cardTitle}</IntroTitle>
            <IntroText>{data.intro.cardText}</IntroText>
          </IntroCard>

          <QuickList>
            {data.intro.quickPoints.map((point) => (
              <QuickItem key={point} $borderColor={borderColor}>
                <FaCheckCircle color={accent} size={18} />
                <span>{point}</span>
              </QuickItem>
            ))}
          </QuickList>
        </IntroGrid>
      </ContentSection>

      <ContentSection id="modulos">
        <SectionHeader>
          <EyebrowTag $background={sectionTagBackground} $accent={accent}>
            {data.modulesSection.tag}
          </EyebrowTag>
          <SectionTitle>{data.modulesSection.title}</SectionTitle>
          <SectionLead>{data.modulesSection.lead}</SectionLead>
        </SectionHeader>

        <ModuleGrid initial="hidden" animate="visible" variants={stagger}>
          {data.modules.map((module) => {
            const Icon = module.icon;

            return (
              <ModuleCard
                key={module.title}
                variants={reveal}
                $borderColor={borderColor}
                $borderStrong={borderStrong}
                $shadowSoft={shadowSoft}
                $shadowStrong={shadowStrong}
              >
                <ModuleIcon $iconBackground={iconBackground} $accent={accent} $shadowSoft={shadowSoft}>
                  <Icon size={24} />
                </ModuleIcon>
                <ModuleTitle>{module.title}</ModuleTitle>
                <ModuleText>{module.description}</ModuleText>
                <BulletList>
                  {module.bullets.map((bullet) => (
                    <Bullet key={bullet}>
                      <FaCheckCircle color={accent} size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{bullet}</span>
                    </Bullet>
                  ))}
                </BulletList>
              </ModuleCard>
            );
          })}
        </ModuleGrid>
      </ContentSection>

      <ContentSection>
        <SectionHeader>
          <EyebrowTag $background={sectionTagBackground} $accent={accent}>
            {data.operations.tag}
          </EyebrowTag>
          <SectionTitle>{data.operations.title}</SectionTitle>
          <SectionLead>{data.operations.lead}</SectionLead>
        </SectionHeader>

        <OperationsGrid>
          <AudienceCard
            $darkBg={darkBg}
            $darkBgSoft={darkBgSoft}
            $borderStrong={borderStrong}
            $shadowStrong={shadowStrong}
          >
            <AudienceTitle>{data.operations.audienceTitle}</AudienceTitle>
            <AudienceText>{data.operations.audienceText}</AudienceText>

            <SectorGrid>
              {data.operations.sectors.map((sector) => {
                const Icon = sector.icon;

                return (
                  <SectorChip key={sector.label}>
                    <Icon size={18} />
                    <span>{sector.label}</span>
                  </SectorChip>
                );
              })}
            </SectorGrid>

            {data.operations.stat ? (
              <AudienceStat>
                <AudienceStatValue>{data.operations.stat.value}</AudienceStatValue>
                <AudienceStatText>{data.operations.stat.text}</AudienceStatText>
              </AudienceStat>
            ) : null}
          </AudienceCard>

          <SupportGrid>
            {data.operations.cards.map((card) => (
              <SupportCard key={card.title} $borderColor={borderColor} $shadowSoft={shadowSoft}>
                <SupportCardTitle>{card.title}</SupportCardTitle>
                <SupportCardText>{card.text}</SupportCardText>
                {card.bullets && card.bullets.length ? (
                  <BulletList>
                    {card.bullets.map((bullet) => (
                      <Bullet key={bullet}>
                        <FaCheckCircle color={accent} size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                        <span>{bullet}</span>
                      </Bullet>
                    ))}
                  </BulletList>
                ) : null}
                {card.link ? (
                  <SupportCardLink href={card.link.href} target={card.link.external ? '_blank' : undefined} rel={card.link.external ? 'noreferrer' : undefined} $accent={accent}>
                    {card.link.label}
                  </SupportCardLink>
                ) : null}
              </SupportCard>
            ))}
          </SupportGrid>
        </OperationsGrid>
      </ContentSection>

      {data.notice ? (
        <ContentSection>
          <NoticeCard $borderColor={borderColor} $shadowSoft={shadowSoft}>
            <NoticeTitle>{data.notice.title}</NoticeTitle>
            <NoticeText>{data.notice.text}</NoticeText>
            {data.notice.badges && data.notice.badges.length ? (
              <BadgeRow>
                {data.notice.badges.map((badge) => (
                  <Badge
                    key={badge}
                    $background={sectionTagBackground}
                    $borderColor={borderColor}
                    $accent={accent}
                  >
                    {badge}
                  </Badge>
                ))}
              </BadgeRow>
            ) : null}
          </NoticeCard>
        </ContentSection>
      ) : null}

      <ContentSection id="contato-produto">
        <ContactCard $borderColor={borderColor} $shadowSoft={shadowSoft}>
          <SectionHeader style={{ marginBottom: 0 }}>
            <EyebrowTag $background={sectionTagBackground} $accent={accent}>
              {data.contact.tag}
            </EyebrowTag>
            <SectionTitle>{data.contact.title}</SectionTitle>
            <SectionLead>{data.contact.lead}</SectionLead>
          </SectionHeader>

          <ContactGrid>
            <ContactItem href="mailto:ecosistemas.computacao@gmail.com" $borderColor={borderColor} $borderStrong={borderStrong} $shadowSoft={shadowSoft}>
              <ContactIcon $iconBackground={hexToRgba(accent, 0.12)} $accent={accent}>
                <FaEnvelope size={20} />
              </ContactIcon>
              <div>
                <ContactLabel>E-mail</ContactLabel>
                <ContactValue>ecosistemas.computacao@gmail.com</ContactValue>
              </div>
            </ContactItem>

            <ContactItem href={WHATSAPP_URL} target="_blank" rel="noreferrer" $borderColor={borderColor} $borderStrong={borderStrong} $shadowSoft={shadowSoft}>
              <ContactIcon $iconBackground={hexToRgba(accent, 0.12)} $accent={accent}>
                <FaPhoneAlt size={18} />
              </ContactIcon>
              <div>
                <ContactLabel>WhatsApp</ContactLabel>
                <ContactValue>(67) 98448-5377</ContactValue>
              </div>
            </ContactItem>

            <ContactItem href="https://www.ecosistemascomputacao.com.br" target="_blank" rel="noreferrer" $borderColor={borderColor} $borderStrong={borderStrong} $shadowSoft={shadowSoft}>
              <ContactIcon $iconBackground={hexToRgba(accent, 0.12)} $accent={accent}>
                <FaGlobe size={18} />
              </ContactIcon>
              <div>
                <ContactLabel>Site</ContactLabel>
                <ContactValue>www.ecosistemascomputacao.com.br</ContactValue>
              </div>
            </ContactItem>
          </ContactGrid>
        </ContactCard>
      </ContentSection>
    </PageContainer>
  );
}

export default ProductPageTemplate;
